import classNames from 'clsx';
import React, { type FC, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ENABLE_FULL_RESPONSIVE } from 'reactApp/appHelpers/configHelpers';
import { enabledPublicDownloadLimits, tooltipTrackDownload } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { getFirstPublicWithDownloadLimits, getPublicLimits } from 'reactApp/modules/public/public.selectors';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { Checkbox } from 'reactApp/ui/DataListItemCommonComponents/Checkbox/Checkbox';
import { Download } from 'reactApp/ui/DataListItemCommonComponents/Download/Download';
import { Publish } from 'reactApp/ui/DataListItemCommonComponents/Publish/Publish';
import { Date } from 'reactApp/ui/DataListItemRow/components/Date/Date';
import { Expires } from 'reactApp/ui/DataListItemRow/components/Expires/Expires';
import { ItemName } from 'reactApp/ui/DataListItemRow/components/ItemName/ItemName';
import { Size } from 'reactApp/ui/DataListItemRow/components/Size/Size';
import { TableColumn } from 'reactApp/ui/DataListItemRow/components/TableColumn/TableColumn';
import type { SharedItemProps } from 'reactApp/ui/DataListItemRow/DataListItemRow.types';
import { FilePic } from 'reactApp/ui/FilePic/FilePic';
import { TrackDownloadCountPromo } from 'reactApp/ui/SharingTooltips/TrackDownloadCountPromo';
import { useCompactView } from 'reactApp/ui/VirtualList/VirtualList.hooks';

import styles from '../../DataListItemRow.new.css';

export const SharedItem: FC<SharedItemProps> = (props) => {
    const {
        id,
        isFolder,
        storage,
        publicExpires,
        ext,
        thumbUrl,
        renameAvailable,
        isAutoDelete,
        mtime,
        kind,
        isVirus,
        isPublic,
        isSeoEnabled,
        name,
        size,
        isSelected,
        onCheckboxClick,
        onPublish,
        onDownload,
        weblinkUrl,
    } = props;

    const isCompact = useCompactView();
    const actionsFlexBasis = ENABLE_FULL_RESPONSIVE ? 40 : 100;
    const tooltipRef = useRef<HTMLDivElement>(null);

    // tempexp_17847-start
    const TOOLTIP_ID = 'tooltipTrackDownload';
    const isTooltipShown = Boolean(storeHelper.getValue(TOOLTIP_ID));
    const [showTooltipCounter, setShowTooltipCounter] = useState(false);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const downloadLimits = useSelector((state: RootState) => getPublicLimits(state, weblinkUrl || ''));
    const downloadTotal = downloadLimits && downloadLimits.total;
    const downloadLeft = downloadLimits && downloadLimits.left;
    const downloadCount = downloadTotal - downloadLeft;
    const showDownloadCounter = enabledPublicDownloadLimits && !isPaidUser && downloadCount > 0;

    const firstPublicWithDownloads = useSelector((state: RootState) => getFirstPublicWithDownloadLimits(state));
    const weblinkForPromo = firstPublicWithDownloads && Object.keys(firstPublicWithDownloads)[0];
    const showTrackDownloadCountPromo = tooltipTrackDownload && showDownloadCounter;
    const showFirstTrackDownloadCountPromo = showTrackDownloadCountPromo && weblinkUrl === weblinkForPromo;

    const handleCounterClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowTooltipCounter(true);
    };

    const handleCounterPromoHide = () => {
        setShowTooltipCounter(false);
    };
    // tempexp_17847-end

    return (
        <>
            <TableColumn>
                <Checkbox viewType={EViewMode.list} className={styles.check} onCheckboxClick={onCheckboxClick} isSelected={isSelected} />
                <FilePic
                    isFolder={isFolder}
                    thumbUrl={thumbUrl}
                    kind={kind}
                    isVirus={isVirus}
                    ext={ext || ''}
                    isAutoDelete={isAutoDelete}
                    publicExpires={publicExpires}
                    isPublic={isPublic}
                />
            </TableColumn>

            {isCompact ? (
                <TableColumn width="100%" overflow="hidden">
                    <div className={styles.nameWrapper}>
                        <ItemName name={name} ext={ext || ''} id={id} isRenameAvailable={renameAvailable} />
                        <span className={styles.responsiveDateWrapper}>
                            <Size size={size} isInline />
                            {!!mtime && <>&nbsp;•&nbsp;</>}
                            <Date date={mtime} className={styles.date} />
                        </span>
                        <Expires expires={publicExpires} isAutoDelete={isAutoDelete} isShared isInline />
                    </div>
                </TableColumn>
            ) : (
                <>
                    <TableColumn width="100%" overflow="hidden">
                        <ItemName name={name} ext={ext || ''} id={id} isRenameAvailable={renameAvailable} />
                    </TableColumn>
                    <TableColumn flexBasis={214} flexShrink={0} justifyContent="flex-start">
                        <Expires expires={publicExpires} isAutoDelete={isAutoDelete} isShared />
                    </TableColumn>
                    <TableColumn flexBasis={140} flexShrink={0} justifyContent="flex-end">
                        <Date date={mtime} />
                    </TableColumn>
                    <TableColumn flexBasis={132} flexShrink={0} justifyContent="center">
                        <Size size={size} />
                    </TableColumn>
                </>
            )}

            <TableColumn flexBasis={actionsFlexBasis} flexShrink={0} justifyContent="flex-end" gap={20}>
                <Publish onClick={onPublish} isShared={isPublic} viewType={EViewMode.list} isSeoEnabled={isSeoEnabled} />
                <Download
                    isAchivedLimit={downloadLeft === 0}
                    onClick={onDownload}
                    ext={isFolder ? 'zip' : ext}
                    size={size}
                    id={id}
                    storage={storage}
                    viewType={EViewMode.list}
                />
            </TableColumn>
            <div
                ref={tooltipRef}
                className={classNames(styles.counter, {
                    [styles.counterAchived]: enabledPublicDownloadLimits && downloadLeft === 0,
                    [styles.counterZero]: enabledPublicDownloadLimits && downloadLeft === downloadTotal,
                })}
                onClick={handleCounterClick}
            >
                {<div className={classNames(styles.counterText)}>{showDownloadCounter ? downloadCount : null}</div>}
            </div>
            {showFirstTrackDownloadCountPromo && !isTooltipShown && (
                <TrackDownloadCountPromo targetRef={tooltipRef} downloads={downloadCount} downloadLimit={downloadTotal} />
            )}
            {showTrackDownloadCountPromo && showTooltipCounter && (
                <TrackDownloadCountPromo
                    targetRef={tooltipRef}
                    downloads={downloadCount}
                    downloadLimit={downloadTotal}
                    onHide={handleCounterPromoHide}
                />
            )}
        </>
    );
};
